/**
 * Sales-funnel helpers shared by every upsell surface in the dashboard.
 *
 * - billingLink(): one canonical deep link into /billing (plan / interval / credits focus / source),
 *   which BillingPage reads to preselect the interval, highlight a card and optionally resume checkout.
 * - trackUpgradeClick(): the `upgrade_cta_clicked {source}` PostHog event every upsell link fires.
 * - registerInterest(): POST /api/billing/interest when a free user clicks a locked feature. Best effort —
 *   the endpoint may not be deployed yet (404) and must never block navigation.
 */
import api from '@/lib/api'
import { track, type SafeAnalyticsProperties } from '@/lib/analytics'

export type PaidPlan = 'solo' | 'professional' | 'business'
export type BillingInterval = 'monthly' | 'annual'
export type InterestFeature =
  | 'ai_video'
  | 'channels'
  | 'white_label'
  | 'custom_domain'
  | 'leads'
  | 'analytics'
  | 'team'
  | 'remove_watermark'

/** Lowest plan that unlocks each premium feature (mirrors workers/vte-api-worker/src/config/plans.ts). */
export const FEATURE_REQUIRED_PLAN: Record<InterestFeature, PaidPlan> = {
  ai_video: 'professional',
  channels: 'business',
  white_label: 'business',
  custom_domain: 'business',
  leads: 'solo',
  analytics: 'professional',
  team: 'professional',
  remove_watermark: 'solo',
}

export const PLAN_LABEL: Record<PaidPlan, string> = {
  solo: 'Solo',
  professional: 'Professional',
  business: 'Business',
}

export interface BillingLinkOptions {
  source: string
  plan?: PaidPlan
  interval?: BillingInterval
  focus?: 'credits'
  /** Credits the user is short of — shown as "You need N more credits". */
  need?: number
  /** Start checkout for `plan` straight away when the billing page loads. */
  resume?: boolean
}

export function billingQuery(opts: BillingLinkOptions): Record<string, string> {
  const query: Record<string, string> = { source: opts.source }
  if (opts.plan) query.plan = opts.plan
  if (opts.interval) query.interval = opts.interval
  if (opts.focus) query.focus = opts.focus
  if (opts.need && opts.need > 0) query.need = String(Math.ceil(opts.need))
  if (opts.resume) query.resume = '1'
  return query
}

/** Router location for <router-link :to> / router.push. */
export function billingLink(opts: BillingLinkOptions) {
  return { path: '/billing', query: billingQuery(opts) }
}

export function trackUpgradeClick(source: string, props: SafeAnalyticsProperties = {}) {
  track('upgrade_cta_clicked', { source, ...props })
}

const interestSent = new Set<string>()

/**
 * Tell the API a free user wanted a locked feature (drives the premium_feature_interest workflow).
 * Deduped per feature+source for the page session; the API dedupes again server-side.
 */
export async function registerInterest(feature: InterestFeature, source: string): Promise<void> {
  const key = `${feature}:${source}`
  if (interestSent.has(key)) return
  interestSent.add(key)
  try {
    await api.post('/api/billing/interest', { feature, source })
  } catch {
    /* endpoint optional (older API) — never block the upsell */
  }
}

/** Click on a locked feature: interest + analytics. Returns the billing link to navigate to. */
export function lockedFeatureClick(feature: InterestFeature, source: string, plan?: PaidPlan) {
  const requiredPlan = plan || FEATURE_REQUIRED_PLAN[feature]
  void registerInterest(feature, source)
  trackUpgradeClick(source, { feature, required_plan: requiredPlan, plan_name: requiredPlan })
  return billingLink({ source, plan: requiredPlan })
}

const paywallSeen = new Set<string>()
/** `paywall_viewed {feature}` once per feature+source per page session. */
export function trackPaywallViewed(feature: string, source: string, requiredPlan?: string) {
  const key = `${feature}:${source}`
  if (paywallSeen.has(key)) return
  paywallSeen.add(key)
  track('paywall_viewed', { feature, source, required_plan: requiredPlan })
}

/** Annual saving in cents for a plan's price pair (monthly × 12 − annual). Never negative. */
export function annualSavingCents(prices: { monthly: { amount: number }; annual: { amount: number } } | null | undefined): number {
  if (!prices) return 0
  return Math.max(0, prices.monthly.amount * 12 - prices.annual.amount)
}
